import { useEffect, useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { Button, Typography, Box } from '@material-ui/core';
import { acceptVehicleInvite } from '../api/vehicleApi';

const translations = {
  en: {
    languageSwitch: 'Slovenščina',
    title: 'Accepting Vehicle Invite...',
    loading: 'Processing invite...',
    success: (vehicleName: string) => (
      <>
        You’ve successfully accepted the vehicle <strong>{vehicleName}</strong>. You can now head
        back to the app to view your vehicle.
      </>
    ),
    noToken: '❌ No token found in link.',
    error: '❌ Failed to accept the invite.',
  },
  sl: {
    languageSwitch: 'English',
    title: 'Sprejemanje povabila za vozilo...',
    loading: 'Obdelovanje povabila...',
    success: (vehicleName: string) => (
      <>
        Uspešno ste sprejeli vozilo <strong>{vehicleName}</strong>. Sedaj se lahko vrnete v
        aplikacijo in si ogledate svoje vozilo.
      </>
    ),
    noToken: '❌ V povezavi ni najdenega žetona.',
    error: '❌ Sprejemanje povabila ni uspelo.',
  },
};

const AcceptInvitePage = () => {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');
  const [vehicleName, setVehicleName] = useState('');
  const location = useLocation();
  const history = useHistory();

  const params = new URLSearchParams(location.search);
  const token = params.get('token');
  const langParam = params.get('lang');

  const [language, setLanguage] = useState<'en' | 'sl'>(langParam === 'en' ? 'en' : 'sl');

  const t = translations[language];

  const toggleLanguage = () => {
    const newLang = language === 'en' ? 'sl' : 'en';
    setLanguage(newLang);

    const newParams = new URLSearchParams(params);
    newParams.set('lang', newLang);
    history.push({
      pathname: location.pathname,
      search: newParams.toString(),
    });
  };

  useEffect(() => {
    const acceptInvite = async () => {
      if (!token) {
        setStatus('error');
        setMessage(t.noToken);
        return;
      }

      setStatus('loading');
      try {
        const res = await acceptVehicleInvite(token);
        const fetchedVehicleName = res?.data?.data?.vehicleName;
        setVehicleName(fetchedVehicleName);
        setStatus('success');
      } catch (err: any) {
        console.error('Invite error:', err?.response || err);
        setStatus('error');
        setMessage(t.error);
      }
    };

    acceptInvite();
  }, [token, t.noToken, t.error]);

  useEffect(() => {
    document.body.classList.add('hide-sidebar');

    return () => {
      document.body.classList.remove('hide-sidebar');
    };
  }, []);

  return (
    <div style={{ padding: 40 }}>
      <Box display="flex" justifyContent="flex-end" mb={3}>
        <Button variant="outlined" color="primary" onClick={toggleLanguage}>
          {t.languageSwitch}
        </Button>
      </Box>

      <Typography variant="h4" gutterBottom>
        {t.title}
      </Typography>

      {status === 'loading' && <Typography>{t.loading}</Typography>}

      {status === 'success' && (
        <Typography style={{ color: 'green' }}>{t.success(vehicleName)}</Typography>
      )}

      {status === 'error' && <Typography style={{ color: 'red' }}>{message}</Typography>}
    </div>
  );
};

export default AcceptInvitePage;
